import React, { createContext, useContext, useState } from 'react';

interface CardCtx {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  collapsible: boolean;
}

const CardContext = createContext<CardCtx>({
  collapsed: false,
  setCollapsed: () => {},
  collapsible: false,
});

interface CardProps {
  className?: string;
  children: React.ReactNode;
  /** Allow header click to collapse content. Default: true (when CardHeader is present). */
  collapsible?: boolean;
  /** Initial collapsed state when collapsible. */
  defaultCollapsed?: boolean;
}

interface CardSubProps {
  className?: string;
  children: React.ReactNode;
}

export function Card({
  className = '',
  children,
  collapsible = true,
  defaultCollapsed = false,
}: CardProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  // Split the header from the rest so we can toggle visibility of everything
  // below the header (including content and any sibling banners between
  // CardHeader and CardContent).
  let headerEl: React.ReactNode = null;
  const rest: React.ReactNode[] = [];
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === CardHeader) {
      headerEl = child;
    } else {
      rest.push(child);
    }
  });

  // Only honour `collapsed` when the card is actually collapsible (has a
  // header AND consumer hasn't opted out). Otherwise content stays visible —
  // otherwise non-collapsible cards (e.g. Login) get stuck hidden.
  const effectivelyCollapsible = !!headerEl && collapsible;
  const isHidden = effectivelyCollapsible && collapsed;

  return (
    <CardContext.Provider value={{ collapsed, setCollapsed, collapsible: effectivelyCollapsible }}>
      <div
        className={`overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-card dark:border-gray-700/60 dark:bg-gray-800 dark:shadow-card-dark ${className}`}
      >
        {headerEl}
        {!isHidden && <div>{rest}</div>}
      </div>
    </CardContext.Provider>
  );
}

export function CardHeader({ className = '', children }: CardSubProps) {
  const { collapsed, setCollapsed, collapsible } = useContext(CardContext);

  // Don't toggle when the click target is an actionable element inside the header
  // (Sync buttons, selects, links, inputs, etc.). Note: we deliberately do NOT
  // include `[role=button]` because the header itself carries that role for a11y;
  // including it would make every click match the header and skip the toggle.
  const isActionable = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    return !!target.closest('button, input, select, textarea, a, label');
  };

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!collapsible) return;
    if (isActionable(e.target)) return;
    setCollapsed(!collapsed);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (!collapsible) return;
    if (isActionable(e.target)) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setCollapsed(!collapsed);
    }
  };

  return (
    <div
      className={`flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-700/60 sm:px-6 sm:py-4 ${
        collapsible ? 'cursor-pointer select-none' : ''
      } ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={collapsible ? 'button' : undefined}
      tabIndex={collapsible ? 0 : undefined}
      aria-expanded={collapsible ? !collapsed : undefined}
    >
      <div className="min-w-0 flex-1">{children}</div>
      {collapsible && (
        <span
          aria-hidden
          className="flex h-6 w-6 items-center justify-center rounded-lg text-sm leading-none text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          {collapsed ? '▸' : '▾'}
        </span>
      )}
    </div>
  );
}

export function CardTitle({ className = '', children }: CardSubProps) {
  return (
    <h3 className={`text-base font-semibold text-gray-900 dark:text-gray-100 ${className}`}>
      {children}
    </h3>
  );
}

export function CardDescription({ className = '', children }: CardSubProps) {
  return <p className={`mt-1 text-sm text-gray-500 dark:text-gray-400 ${className}`}>{children}</p>;
}

export function CardContent({ className = '', children }: CardSubProps) {
  return <div className={`px-4 py-4 sm:px-6 sm:py-5 ${className}`}>{children}</div>;
}
